import { addNotification, addPageError } from '../stores'
import { DefaultNotification, NotificationType } from '$types/notification'

export const fetchUtils = {
	get,
	post,
	put,
	delete: _delete,
	showError,
	catchErrorAndShowNotification,
	postAndDownload
}

export type FetchError = {
	code: number,
	message: string,
	data: object
}

function showError(page, customMessage) {
	return (error) => {
		console.error(error)
		page.showErrorMsg(customMessage ?? error.message ?? error)
	}
}

function get(url: string, publishPageError = true) {
	const requestOptions = {
		method: 'GET'
	}
	return fetch(url, requestOptions).then(doHandle(publishPageError))
}

function post(url: string, body, publishPageError = true, stringify = true) {
	const bodyData = stringify ? JSON.stringify(body) : body
	const requestOptions = {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: bodyData
	}
	return fetch(url, requestOptions).then(doHandle(publishPageError))
}

async function postAndDownload(
	url: string,
	body: any,
	defaultFilename: string,
	publishPageError = true,
	stringify = true
) {
	const bodyData = stringify ? JSON.stringify(body) : body
	const requestOptions = {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json'
		},
		body: bodyData
	}
	const response = await fetch(url, requestOptions)
	if (response.status === 200 || response.ok) {
		const blob = await response.blob()
		_downloadBlob(blob, _getFilenameFromResponse(response) || defaultFilename)
		return Promise.resolve()
	} else {
		const json = await response.json()
		const error = json?.errorMessage || response.statusText
		const currentError = {
			code: response.status,
			message: error,
			data: json.data
		} as FetchError
		if (response.status in SpecialHandledErrors && publishPageError) {
			addPageError(currentError)
		}
		return Promise.reject(currentError)
	}
}

function put(url: string, body, publishPageError = true) {
	const requestOptions = {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body)
	}
	return fetch(url, requestOptions).then(doHandle(publishPageError))
}

const CONTENT_DISPOSITION_FILENAME_REGEX = /filename="(.+)"/

// prefixed with underscored because delete is a reserved word in javascript
function _delete(url: string, publishPageError = true) {
	const requestOptions = {
		method: 'DELETE'
	}
	return fetch(url, requestOptions).then(doHandle(publishPageError))
}

function _downloadBlob(blob, filename) {
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = filename
	a.click()
	URL.revokeObjectURL(url)
}

function _getFilenameFromResponse(response) {
	const contentDisposition = response.headers.get('Content-Disposition')
	if (contentDisposition) {
		const match = contentDisposition.match(CONTENT_DISPOSITION_FILENAME_REGEX)
		return match ? match[1] : null
	}
	return null
}

function catchErrorAndShowNotification(target: string | null = null) {
	return (error) => {
		const notification = DefaultNotification(
			error.message ?? error,
			NotificationType.ERROR,
			true,
			0,
			target
		)
		addNotification(notification)
	}
}

export enum SpecialHandledErrors {
	NOT_AUTHORIZED = 401,
	MISSING_PERMISSION = 403,
	NOT_FOUND = 404,
	GONE = 410
}

// helper functions
export function doHandle(publishPageError: boolean) {
	return function (response: Response): JSONResponse | Promise<any> {
		return response.json().then((json: JSONResponse) => {
			if (!response.ok || (!json.success && !json)) {
				const error = json?.errorMessage || response.statusText
				const currentError = {
					code: response.status,
					message: error,
					data: json.data
				}
				console.error(currentError)
				if (response.status in SpecialHandledErrors && publishPageError) {
					addPageError(currentError)
				}
				return Promise.reject(currentError)
			}

			// ?? (nullish coalescing — only falls back on null/undefined, not false)
			// -> data is not enveloped with success, errorCode, etc.
			return json?.data ?? json
		})
	}
}

type JSONResponse = {
	data: any
	errorCode: number
	hasValidationErrors: boolean
	success: boolean
	successCode: any
	validationErrors: any[]
	errorMessage?: string
}
